<template>
  <v-container>
    <v-sheet class="sticky">
      <v-toolbar flat>
        <v-text-field
          v-model="search"
          prepend-icon="mdi-magnify"
          clearable
          label="Search"
          single-line
          hide-details
        ></v-text-field>
        <v-col cols="12" sm="4" class="px-0">
          <v-switch
            v-model="activePlansFilter"
            x-small
            hide-details
            class="mx-1 mt-1"
          >
            <template v-slot:label>
              <span class="text-body-2">Active Plans only</span></template
            >
          </v-switch>
        </v-col>
        <v-spacer></v-spacer>
        <download-csv-btn
          :headers="_headers"
          :items="deduplicatedAndFilteredList"
          :fileName="'ClientRoster.csv'"
        ></download-csv-btn>
      </v-toolbar>
      <v-toolbar flat>
        <v-row>
          <v-col cols="12" sm="2">
            <work-views :columns="headers"></work-views
          ></v-col>
          <v-col cols="12" sm="1">
            <client-roster-display :columns="headers"></client-roster-display
          ></v-col>
          <v-col cols="12" sm="9">
            <client-roster-filters></client-roster-filters
          ></v-col>
        </v-row>
      </v-toolbar>
    </v-sheet>
    <v-data-table
      :headers="_headers"
      :items="deduplicatedAndFilteredList"
      :search="search"
      :loading="loading"
      :items-per-page="10"
      dense
      multi-sort
      item-key="uniqueId"
    >
      <template v-for="tab in tabs" v-slot:[`item.${tab.name}`]="{ item }">
        <div
          :key="tab.id"
          class="text-decoration-underline indigo--text"
          style="cursor: pointer"
          @click="togglePanel(tab, item[tab.param])"
        >
          {{ item[tab.name] }}
        </div>
      </template>
      <template v-slot:[`item.FADSubmitted`]="{ item }">
        <span
          v-if="Boolean(item.firstAttendDateSubmittedAt)"
          class="text-body-2 font-weight-light"
          >{{ item.formattedFADSubmitted }}</span
        >
        <v-checkbox
          v-else
          :value="Boolean(item.firstAttendDateSubmittedAt)"
          x-small
          :disabled="!Boolean(item.formattedFAD)"
          @change="setFADSubmitted(item, $event)"
        ></v-checkbox>
      </template>
    </v-data-table>
    <client-roster-sidebar
      v-if="Boolean(activeComponent)"
      :activeComponent="activeComponent"
      :schoolId="Boolean(selectedSchoolId) ? selectedSchoolId : null"
      :clientId="Boolean(selectedClientId) ? selectedClientId : null"
      :districtAdminId="
        Boolean(selectedDistrictAdminId) ? selectedDistrictAdminId : null
      "
      :providerId="Boolean(selectedProviderId) ? selectedProviderId : null"
      @closePanel="togglePanel()"
    ></client-roster-sidebar>
  </v-container>
</template>

<script>
import ClientRosterFilters from "./client-roster-filters.vue";
import ClientRosterDisplay from "./client-roster-display.vue";
import DownloadCsvBtn from "../Shared/download-csv-btn.vue";
import ClientRosterSidebar from "./client-roster-sidebar.vue";
import WorkViews from "./work-views";
export default {
  components: {
    ClientRosterFilters,
    ClientRosterDisplay,
    DownloadCsvBtn,
    ClientRosterSidebar,
    WorkViews,
  },
  data: function () {
    return {
      search: "",
      loading: false,
      activeComponent: null,
      selectedSchoolId: null,
      selectedClientId: null,
      selectedDistrictAdminId: null,
      selectedProviderId: null,
    };
  },
  async mounted() {
    this.loading = true;
    await this.$store.dispatch("plansModule/getClientRoster", { programId: 6 });
    this.loading = false;
  },
  computed: {
    headers() {
      return [
        {
          text: "Client Name",
          value: "clientName",
          workviewTags: [
            "#expiring",
            "#pendingAssignment",
            "#pendingApproval",
            "#awaitingFADSubmission",
          ],
        },
        {
          text: "DOB",
          value: "formattedDob",
          sortable: false,
          workviewTags: [
            "#expiring",
            "#pendingAssignment",
            "#pendingApproval",
            "#awaitingFADSubmission",
          ],
        },
        {
          text: "OSIS",
          value: "formattedOsis",
          sortable: false,
          workviewTags: [
            "#expiring",
            "#pendingAssignment",
            "#pendingApproval",
            "#awaitingFADSubmission",
          ],
        },
        {
          text: "District Admin",
          value: "districtAdminFullName",
          workviewTags: ["#pendingAssignment", "#pendingApproval"],
        },
        {
          text: "District",
          value: "districtId",
          sortable: false,
          workviewTags: ["#pendingAssignment"],
        },
        {
          text: "School",
          value: "schoolName",
          workviewTags: ["#pendingAssignment", "#pendingApproval"],
        },
        {
          text: "Team",
          value: "teamName",
          workviewTags: ["#pendingAssignment", "#pendingApproval"],
        },
        {
          text: "Service",
          value: "serviceCode",
          sortable: false,
          workviewTags: [
            "#expiring",
            "#pendingAssignment",
            "#pendingApproval",
            "#awaitingFADSubmission",
          ],
        },
        {
          text: "Plan Type",
          value: "planType",
          sortable: false,
          workviewTags: [
            "#expiring",
            "#pendingAssignment",
            "#pendingApproval",
            "#awaitingFADSubmission",
          ],
        },
        {
          text: "Plan Approval Start",
          value: "formattedServicePlanStartDate",
          sort: (a, b) => this.sortByDate(a, b),
          workviewTags: [
            "#expiring",
            "#pendingAssignment",
            "#pendingApproval",
            "#awaitingFADSubmission",
          ],
        },
        {
          text: "Plan Approval End",
          value: "formattedServicePlanEndDate",
          sort: (a, b) => this.sortByDate(a, b),
          workviewTags: [
            "#expiring",
            "#pendingAssignment",
            "#pendingApproval",
            "#awaitingFADSubmission",
          ],
        },
        {
          text: "Mandate",
          value: "formattedMandate",
          sortable: false,
          workviewTags: ["#expiring", "#pendingAssignment", "#pendingApproval"],
        },
        {
          text: "Group Size",
          value: "groupSize",
          workviewTags: ["#expiring", "#pendingAssignment", "#pendingApproval"],
        },
        {
          text: "10/12 Month",
          value: "tenTwelveMonth",
          workviewTags: [
            "#expiring",
            "#pendingAssignment",
            "#pendingApproval",
            "#awaitingFADSubmission",
          ],
        },
        { text: "Language", value: "language", workviewTags: [, "#pendingAssignment"] },
        {
          text: "Location",
          value: "location",
          workviewTags: ["#expiring", "#pendingAssignment", "#pendingApproval"],
        },
        {
          text: "Semester",
          value: "semesterCode",
          workviewTags: [
            "#pendingAssignment",
            "#pendingApproval",
            "#awaitingFADSubmission",
          ],
        },
        {
          text: "Provider",
          value: "providerFullName",
          workviewTags: ["#expiring", "#pendingApproval"],
        },
        {
          text: "Assignment Approval Start",
          value: "formattedApprovalStartDate",
          sort: (a, b) => this.sortByDate(a, b),
          workviewTags: ["#expiring"],
        },
        {
          text: "Assignment Approval End",
          value: "formattedApprovalEndDate",
          sort: (a, b) => this.sortByDate(a, b),
          workviewTags: ["#expiring"],
        },
        {
          text: "FAD",
          value: "formattedFAD",
          sort: (a, b) => this.sortByDate(a, b),
          workviewTags: ["#awaitingFADSubmission"],
        },
        {
          text: "FAD Submitted",
          value: "FADSubmitted",
          sortable: false,
          workviewTags: ["#awaitingFADSubmission"],
        },
      ];
    },
    tabs() {
      return [
        {
          id: 1,
          name: "schoolName",
          param: "schoolId",
          component: "SchoolForm",
        },
        {
          id: 2,
          name: "clientName",
          param: "clientId",
          component: "ClientDetails",
        },
        {
          id: 3,
          name: "districtAdminFullName",
          param: "districtAdminId",
          component: "DistrictAdminForm",
        },
        {
          id: 4,
          name: "providerFullName",
          param: "providerId",
          component: "ProviderForm",
        },
      ];
    },
    filteredRoster() {
      return this.$store.getters["plansModule/getFilteredRoster"];
    },
    displayColumns() {
      return this.$store.state.plansModule.displayColumns;
    },
    _headers() {
      return this.headers.filter((h) => this.displayColumns.includes(h.value));
    },
    deduplicatedAndFilteredList() {
      const seen = new Set();
      return this.filteredRoster.filter((record) => {
        const key = this.displayColumns.map((f) => record[f]).join("|");
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },
    activePlansFilter: {
      get() {
        return this.$store.state.plansModule.filters.activePlansFilter;
      },
      set(value) {
        this.$store.dispatch("plansModule/setFilters", {
          filterName: "activePlansFilter",
          filterValue: value,
        });
      },
    },
  },
  methods: {
    sortByDate(a, b) {
      const dateA = new Date(a);
      const dateB = new Date(b);
      if (dateA < dateB) return -1;
      if (dateA > dateB) return 1;
      return 0;
    },
    togglePanel(item, id) {
      if (Boolean(item)) {
        this.activeComponent = item.component;
        switch (item.param) {
          case "schoolId":
            this.selectedSchoolId = id;
          case "clientId":
            this.selectedClientId = id;
          case "districtAdminId":
            this.selectedDistrictAdminId = id;
          case "providerId":
            this.selectedProviderId = id;
        }
      } else {
        this.activeComponent = null;
      }
    },
    async setFADSubmitted(item, event) {
      this.$store.commit("uxModule/setShowLoader", true);
      var message = await this.$store.dispatch("plansModule/setFADSubmitted", {
        servicePlanId: item.servicePlanId,
        semesterId: item.semesterId,
        submitted: event,
      });

      if (message) {
        this.$store.commit("uxModule/setSnackbarMsg", message);
        this.$store.commit("uxModule/setShowSnackbar", true);
      }
      this.$store.commit("uxModule/setShowLoader", false);
    },
  },
};
</script>
<style scoped>
.sticky {
  background-color: white;
  position: sticky;
  top: 60px;
  z-index: 1;
}
</style>
